import { Tabs, TabsList, TabsTrigger } from '@parrot-software/design-system';
import { useHistory } from 'react-router-dom';

export interface TabRoute {
  value: string;
  label: string;
  path: string;
}

export interface TabsRouterProps {
  tabs: TabRoute[];
  activeTab: string;
  onTabChange?: (tab: string) => void;
}

/**
 * Tabs Router Component
 *
 * Generic wrapper around design-system Tabs that adds route navigation functionality.
 * Automatically navigates to the specified path when a tab is clicked.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * This is a reusable component that can be used across different domains.
 *
 * @param {TabsRouterProps} props - Component properties
 * @param {TabRoute[]} props.tabs - Array of tab configurations with value, label, and path
 * @param {string} props.activeTab - Currently active tab value
 * @param {Function} [props.onTabChange] - Optional callback when tab changes (called before navigation)
 * @returns {React.ReactElement} Tabs router component
 *
 * @example
 * <TabsRouter
 *   activeTab="dashboard"
 *   tabs={[
 *     { value: 'overview', label: 'Overview', path: '/dashboard/overview' },
 *     { value: 'analytics', label: 'Analytics', path: '/dashboard/analytics' }
 *   ]}
 *   onTabChange={(tab) => console.log('Changing to:', tab)}
 * />
 */
const styles = {
  list: 'bg-muted p-1.5 rounded-md inline-flex w-fit h-auto',
  trigger:
    'px-3 py-1.5 text-sm font-medium rounded-xs data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs',
};

export const TabsRouter = ({ tabs, activeTab, onTabChange }: TabsRouterProps): React.ReactElement => {
  const history = useHistory();

  const handleValueChange = (value: string) => {
    // Call optional callback first
    if (onTabChange) {
      onTabChange(value);
    }

    // Find the tab and navigate to its path
    const selectedTab = tabs.find((tab) => tab.value === value);
    if (selectedTab) {
      history.push(selectedTab.path);
    }
  };

  return (
    <Tabs onValueChange={handleValueChange} value={activeTab}>
      <TabsList className={styles.list}>
        {tabs.map((tab) => (
          <TabsTrigger className={styles.trigger} key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
